import { type SelectOptionType } from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSchoolYearsQuery } from '../types/planung-graphql-client-defs';
import { useCreateSelectOptions } from './useCreateSelectOptions';
import { useMemorizedCacheTag } from './useMemorizedCacheTag';
import { useUserConfigContext } from './useUserConfigContext';

/**
 * Schuljahre der Organisation + ausgewähltes Schuljahr, gemeinsam genutzt von Header-Select (Desktop),
 * Schuljahr-Chip (Top-Bar mobil) und Menü.
 */
export const useSchoolYearSelect = () => {
  const { t } = useTranslation();
  const userConfig = useUserConfigContext();
  const context = useMemorizedCacheTag('SCHOOL_YEAR');

  const [{ data }] = useSchoolYearsQuery({
    context: context,
  });

  const schoolYears = data?.schoolYears;
  const options: SelectOptionType[] = useCreateSelectOptions(schoolYears, 'uuid', 'name');

  const selectedOption: SelectOptionType = useMemo(
    () =>
      options.find((s) => s.value === userConfig.selectedSchoolYear?.uuid) ?? {
        value: '',
        label: t('common.unavailable'),
      },
    [options, userConfig.selectedSchoolYear?.uuid, t],
  );

  const selectedSchoolYear = schoolYears?.find((schoolYear) => schoolYear.uuid === userConfig.selectedSchoolYear?.uuid);

  const selectSchoolYear = (uuid: string) => {
    const schoolYear = schoolYears?.find((schoolYear) => schoolYear.uuid === uuid);
    void userConfig?.setSchoolYear(schoolYear ?? null);
  };

  return {
    options,
    selectedOption,
    /** Kurzbezeichnung aus den Stammdaten (z. B. für den Schuljahr-Chip) */
    selectedShortName: selectedSchoolYear?.shortName || selectedOption.label,
    isDisabled: !schoolYears?.length,
    selectSchoolYear,
  };
};
